import { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { FaRegTrashAlt, FaRegEdit } from "react-icons/fa";
import { MdAdd, MdClose, MdCheck } from "react-icons/md";
import { ImSpinner2 } from "react-icons/im";
import { FiShare2 } from "react-icons/fi";
import PanelSkeleton from "../PanelSkeleton";
import styles from "../../styles/salesChannelsPanel.module.css";

const PRESET_COLORS = [
  "#1877f2", "#25d366", "#e1306c", "#0088cc",
  "#ff6900", "#7c3aed", "#16a34a", "#dc2626",
];

const DEFAULT_FORM = { name: "", icon: "", color: "#c98a52" };

const SalesChannelsPanel = () => {
  const [channels, setChannels]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [deleting, setDeleting]   = useState(false);
  const [editId, setEditId]       = useState(null);
  const [form, setForm]           = useState(DEFAULT_FORM);
  const [showForm, setShowForm]   = useState(false);
  const nameRef = useRef(null);

  const token = useSelector((s) => s.auth?.token);

  const authHeaders = { withCredentials: true, headers: { Authorization: `Bearer ${token}` } };

  const fetchChannels = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/sales-channels`);
      setChannels(res.data?.data ?? []);
    } catch {
      toast.error(<span className="toast">فشل تحميل قنوات البيع</span>);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchChannels(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openAdd = () => {
    setEditId(null);
    setForm(DEFAULT_FORM);
    setShowForm(true);
    setTimeout(() => nameRef.current?.focus(), 80);
  };

  const openEdit = (ch) => {
    setEditId(ch.id);
    setForm({ name: ch.name, icon: ch.icon ?? "", color: ch.color ?? "#c98a52" });
    setShowForm(true);
    setTimeout(() => nameRef.current?.focus(), 80);
  };

  const closeForm = () => { setShowForm(false); setEditId(null); setForm(DEFAULT_FORM); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const payload = { name: form.name.trim(), icon: form.icon.trim() || null, color: form.color || null };
      if (editId) {
        await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/sales-channels/${editId}`, payload, authHeaders);
        toast.success(<span className="toast">تم تعديل القناة</span>);
      } else {
        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/sales-channels`, payload, authHeaders);
        toast.success(<span className="toast">تم إضافة القناة</span>);
      }
      closeForm();
      fetchChannels();
    } catch {
      toast.error(<span className="toast">فشلت العملية. حاول مرة أخرى</span>);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (ch) => {
    confirmAlert({
      message: `حذف قناة "${ch.name}" ؟`,
      buttons: [
        {
          label: "نعم",
          onClick: async () => {
            setDeleting(true);
            try {
              await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/sales-channels/${ch.id}`, authHeaders);
              toast.success(<span className="toast">تم حذف القناة</span>);
              fetchChannels();
            } catch {
              toast.error(<span className="toast">فشل الحذف. حاول مرة أخرى</span>);
            } finally {
              setDeleting(false);
            }
          },
        },
        { label: "لا" },
      ],
    });
  };

  return (
    <div className={styles.panel}>

      {/* ── Header ── */}
      <div className={styles.panelHead}>
        <span className={styles.panelTitle}>
          <FiShare2 className={styles.panelTitleIcon} />
          قنوات البيع
        </span>
        <button className={styles.addBtn} onClick={openAdd} disabled={showForm}>
          <MdAdd /> إضافة قناة
        </button>
      </div>

      {/* ── Add / Edit Form ── */}
      {showForm && (
        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.formHead}>
            <span className={styles.formTitle}>{editId ? "تعديل القناة" : "قناة جديدة"}</span>
            <button type="button" className={styles.formClose} onClick={closeForm}>
              <MdClose />
            </button>
          </div>

          <div className={styles.formRow}>
            <label className={styles.formLabel}>اسم القناة *</label>
            <input
              ref={nameRef}
              className={styles.formInput}
              placeholder="مثال: فيسبوك"
              maxLength={50}
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              required
            />
          </div>

          <div className={styles.formRow}>
            <label className={styles.formLabel}>إيموجي (اختياري)</label>
            <input
              className={`${styles.formInput} ${styles.emojiInput}`}
              placeholder="مثال: 📘"
              maxLength={10}
              value={form.icon}
              onChange={(e) => setForm((f) => ({ ...f, icon: e.target.value }))}
            />
          </div>

          <div className={styles.formRow}>
            <label className={styles.formLabel}>اللون</label>
            <div className={styles.colorRow}>
              {PRESET_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`${styles.colorSwatch}${form.color === c ? ` ${styles.colorSwatchActive}` : ""}`}
                  style={{ background: c }}
                  onClick={() => setForm((f) => ({ ...f, color: c }))}
                >
                  {form.color === c && <MdCheck className={styles.colorCheck} />}
                </button>
              ))}
              <input
                type="color"
                className={styles.colorPicker}
                value={form.color}
                onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                title="اختيار لون مخصص"
              />
            </div>
          </div>

          <div className={styles.formPreview}>
            <span className={styles.previewLabel}>معاينة:</span>
            <span
              className={styles.channelBadge}
              style={{ background: `${form.color}18`, color: form.color, borderColor: `${form.color}44` }}
            >
              {form.icon && <span>{form.icon}</span>}
              {form.name || "اسم القناة"}
            </span>
          </div>

          <div className={styles.formActions}>
            <button type="submit" className={styles.saveBtn} disabled={saving}>
              {saving ? <ImSpinner2 className="smallspinner" /> : <><MdCheck /> حفظ</>}
            </button>
            <button type="button" className={styles.cancelFormBtn} onClick={closeForm}>
              إلغاء
            </button>
          </div>
        </form>
      )}

      {/* ── Channel List ── */}
      {loading ? (
        <PanelSkeleton count={3} />
      ) : channels.length === 0 ? (
        <p className="warn">لا توجد قنوات بيع بعد. أضف أول قناة!</p>
      ) : (
        <div className={styles.list}>
          {channels.map((ch, i) => (
            <div key={ch.id} className={styles.channelRow} style={{ animationDelay: `${i * 0.05}s` }}>
              <span
                className={styles.channelBadge}
                style={{
                  background: ch.color ? `${ch.color}18` : "var(--surface-2)",
                  color: ch.color ?? "var(--ink)",
                  borderColor: ch.color ? `${ch.color}44` : "var(--line-strong)",
                }}
              >
                {ch.icon && <span>{ch.icon}</span>}
                {ch.name}
              </span>

              <span className={styles.channelMeta}>
                {ch.color && (
                  <span className={styles.colorDot} style={{ background: ch.color }} title={ch.color} />
                )}
              </span>

              <div className={styles.channelActions}>
                <button
                  className={styles.editBtn}
                  onClick={() => openEdit(ch)}
                  disabled={deleting}
                  title="تعديل"
                >
                  <FaRegEdit />
                </button>
                <button
                  className={styles.deleteBtn}
                  onClick={() => !deleting && handleDelete(ch)}
                  disabled={deleting}
                  title="حذف"
                >
                  <FaRegTrashAlt />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <p className={styles.hint}>
        قنوات البيع تُستخدم لتتبع مصدر الطلبات (فيسبوك، واتساب، إلخ). الطلبات من الموقع لا تحتاج قناة.
      </p>
    </div>
  );
};

export default SalesChannelsPanel;
